import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  Archive,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Eye,
  Pencil,
  Play,
  RefreshCw,
  Upload,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { thicknessConditionsSchema } from '@/features/cad/schemas/thicknessConditions.schema';
import CadLimitsPanel from './CadLimitsPanel';
import { useCapabilities } from '@hooks/useCapabilities';
import {
  createThicknessBatch,
  downloadThicknessBatchZip,
  getCADModel,
  getThicknessAnalysis,
  listThicknessBatches,
  reanalyzeThicknessBatchItem,
  retryThicknessBatchItem,
} from '../../services/cad.service';
import { ThicknessBatchItem, ThicknessBatchJob } from '../../types/cad';
import { resolveApiErrorMessage } from '../../utils/apiErrorMessages';
import {
  makeCadFileSchema,
} from '@/features/cad/schemas/cadFile.schema';
import { CADThicknessUploadQueueProps } from './thickness-upload/cadThicknessQueue.types';
import CADClassificationFields from './CADClassificationFields';
import ThicknessBatchClassificationDialog from './ThicknessBatchClassificationDialog';
import {
  EMPTY_CLASSIFICATION_DRAFT,
  classificationFromDraft,
  normalizeClassificationSearch,
  type CADClassificationDraft,
} from './classification';

const ACTIVE_BATCH_STATUSES = new Set(['queued', 'processing']);

const itemBadge = (item: ThicknessBatchItem, t: TFunction) => {
  if (item.status === 'failed') {
    return <Badge variant="destructive">{t('cad.thickness.batch.status.failed')}</Badge>;
  }
  if (item.status !== 'completed') {
    return <Badge variant="info">{t('cad.thickness.batch.status.processing')}</Badge>;
  }
  if (item.judgment === 'needs_review') {
    return <Badge variant="warning">{t('cad.thickness.batch.status.needs_review')}</Badge>;
  }
  return <Badge variant="success">{t('cad.thickness.batch.status.completed')}</Badge>;
};

const MAX_REASONS = 5;

const CADThicknessUploadQueue: React.FC<CADThicknessUploadQueueProps> = ({
  canAnalyze,
  onModelAvailable,
  onPipelineComplete,
  onRunningChange,
}) => {
  const { t, i18n } = useTranslation();
  const capabilityState = useCapabilities();
  const cad = capabilityState.status === 'ready' ? capabilityState.capabilities?.cad : undefined;
  const canSelect = Boolean(cad?.enabled && cad.thicknessEnabled && canAnalyze);
  const inputRef = useRef<HTMLInputElement>(null);
  const initializedExpandedJobs = useRef(false);
  const initializedCreateForm = useRef(false);
  const [files, setFiles] = useState<File[]>([]);
  const [name, setName] = useState('');
  const [classification, setClassification] = useState<CADClassificationDraft>(
    EMPTY_CLASSIFICATION_DRAFT,
  );
  const [lowerMm, setLowerMm] = useState(2);
  const [upperMm, setUpperMm] = useState(3);
  const [toleranceMm, setToleranceMm] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const [showAllJobIds, setShowAllJobIds] = useState<Set<string>>(new Set());
  const [expandedJobIds, setExpandedJobIds] = useState<Set<string>>(new Set());
  const [partSearch, setPartSearch] = useState('');
  const [machineFilter, setMachineFilter] = useState('');
  const [numberFilter, setNumberFilter] = useState('');
  const [jobs, setJobs] = useState<ThicknessBatchJob[]>([]);
  const [classificationJob, setClassificationJob] = useState<ThicknessBatchJob | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The FILES, not their messages: re-parsing during render re-resolves the
  // schema's text, so the notice follows a language change like the rest of #1175.
  const [rejected, setRejected] = useState<{ files: File[]; capped: number }>({ files: [], capped: 0 });
  const [acceptedMessage, setAcceptedMessage] = useState<string | null>(null);
  const [overrideConfigs, setOverrideConfigs] = useState<Record<string, {
    lowerMm: number;
    upperMm: number;
    toleranceMm: number;
  }>>({});

  const normalizedPartSearch = normalizeClassificationSearch(partSearch);
  const machineOptions = useMemo(() => Array.from(new Set(
    jobs.map((job) => job.classification.machineModel).filter(Boolean),
  )).sort(), [jobs]);
  const numberOptions = useMemo(() => Array.from(new Set(
    jobs.map((job) => job.classification.managementNumber).filter(Boolean),
  )).sort(), [jobs]);
  const activeMachineFilter = machineFilter && !machineOptions.includes(machineFilter)
    ? ''
    : machineFilter;
  const activeNumberFilter = numberFilter && !numberOptions.includes(numberFilter)
    ? ''
    : numberFilter;
  const hasActiveBatchFilter = Boolean(
    normalizedPartSearch || activeMachineFilter || activeNumberFilter,
  );
  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      if (activeMachineFilter
        && job.classification.machineModel !== activeMachineFilter) return false;
      if (activeNumberFilter
        && job.classification.managementNumber !== activeNumberFilter) return false;
      if (!normalizedPartSearch) return true;
      return [
        job.name,
        job.classification.folderPath,
        job.classification.machineModel,
        job.classification.managementNumber,
        ...job.classification.tags,
        ...job.items.map((item) => item.filename),
      ].some((value) => (
        normalizeClassificationSearch(value).includes(normalizedPartSearch)
      ));
    });
  }, [activeMachineFilter, activeNumberFilter, jobs, normalizedPartSearch]);

  const refreshJobs = useCallback(async () => {
    try {
      const loaded = await listThicknessBatches();
      setJobs(loaded);
      if (!initializedCreateForm.current) {
        initializedCreateForm.current = true;
        setShowCreateForm(loaded.length === 0);
      }
      if (!initializedExpandedJobs.current) {
        initializedExpandedJobs.current = true;
        if (loaded[0]) setExpandedJobIds(new Set([loaded[0].jobId]));
      }
      setError(null);
    } catch (refreshError) {
      setError(resolveApiErrorMessage(t, refreshError, {
        fallbackKey: 'cad.thickness.batch.loadFailed',
      }));
    } finally {
      setLoadingJobs(false);
    }
  }, [t]);

  useEffect(() => {
    refreshJobs();
  }, [refreshJobs]);

  useEffect(() => {
    if (!jobs.some((job) => ACTIVE_BATCH_STATUSES.has(job.status))) return undefined;
    const timer = window.setInterval(refreshJobs, 3000);
    return () => window.clearInterval(timer);
  }, [jobs, refreshJobs]);

  useEffect(() => {
    onRunningChange?.(submitting);
  }, [onRunningChange, submitting]);

  // One file can break BOTH rules, so every issue counts, not just the first.
  // Memoised on the selection and on `t`: re-parsing is what lets the text follow
  // a language change, and it must not run on every 3-second job poll.
  const rejectionNotice = useMemo(() => {
    if (!cad || (!rejected.files.length && !rejected.capped)) return null;
    const lines: string[] = [];
    if (rejected.files.length) {
      const reasons = Array.from(new Set(rejected.files.flatMap((file) => {
        const result = makeCadFileSchema(cad).safeParse(file);
        return result.success ? [] : result.error.issues.map((i) => i.message);
      })));
      const shown = reasons.slice(0, MAX_REASONS);
      if (reasons.length > MAX_REASONS) {
        shown.push(t('cad.upload.moreReasons', { count: reasons.length - MAX_REASONS }));
      }
      lines.push(t('cad.upload.rejectedFiles', {
        count: rejected.files.length,
        reasons: shown.join('\n'),
      }));
    }
    if (rejected.capped) lines.push(t('cad.upload.batchLimited', { max: cad.maxBatchFiles }));
    return lines.join('\n');
  }, [cad, rejected, t]);

  const handleFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = '';
    if (!cad || !canSelect) return;
    const accepted: File[] = [];
    const refused: File[] = [];
    selected.forEach((file) => {
      (makeCadFileSchema(cad).safeParse(file).success ? accepted : refused).push(file);
    });
    setRejected({
      files: refused,
      capped: Math.max(0, accepted.length - cad.maxBatchFiles),
    });
    setError(null);
    setFiles(accepted.slice(0, cad.maxBatchFiles));
  };

  const startBatch = async () => {
    if (!files.length || !cad || !canSelect) return;
    const conditions = thicknessConditionsSchema.safeParse({
      lowerMm,
      upperMm,
      toleranceMm,
    });
    if (!conditions.success) {
      setError(conditions.error.issues[0].message);
      return;
    }
    setSubmitting(true);
    setError(null);
    setAcceptedMessage(null);
    try {
      const job = await createThicknessBatch({
        // The server would otherwise persist a Japanese default for every language.
        name: name.trim() || t('cad.thickness.batch.defaultName', {
          timestamp: new Date().toLocaleString(i18n.language === 'en' ? 'en-US' : 'ja-JP', {
            dateStyle: 'medium',
            timeStyle: 'short',
          }),
        }),
        files,
        lowerMm,
        upperMm,
        judgmentToleranceMm: toleranceMm,
        classification: classificationFromDraft(classification),
      });
      setJobs((current) => [job, ...current.filter((item) => item.jobId !== job.jobId)]);
      setExpandedJobIds((current) => new Set(current).add(job.jobId));
      setFiles([]);
      setRejected({ files: [], capped: 0 });
      setName('');
      setClassification(EMPTY_CLASSIFICATION_DRAFT);
      setShowCreateForm(false);
      setAcceptedMessage(
        t('cad.thickness.batch.accepted', { count: job.items.length }),
      );
    } catch (submitError) {
      setError(resolveApiErrorMessage(t, submitError, {
        fallbackKey: 'cad.thickness.batch.startFailed',
      }));
    } finally {
      setSubmitting(false);
    }
  };

  const openItem = async (item: ThicknessBatchItem) => {
    if (!item.analysisId) return;
    try {
      const [model, analysis] = await Promise.all([
        getCADModel(item.modelId),
        getThicknessAnalysis(item.modelId, item.analysisId),
      ]);
      onModelAvailable(model);
      onPipelineComplete(model, analysis);
    } catch (openError) {
      setError(resolveApiErrorMessage(t, openError, {
        fallbackKey: 'cad.thickness.batch.openFailed',
      }));
    }
  };

  const retryItem = async (jobId: string, itemId: string) => {
    try {
      const updated = await retryThicknessBatchItem(jobId, itemId);
      setJobs((current) => current.map((job) => (
        job.jobId === updated.jobId ? updated : job
      )));
    } catch (retryError) {
      setError(resolveApiErrorMessage(t, retryError, {
        fallbackKey: 'cad.thickness.batch.retryFailed',
      }));
    }
  };

  const reanalyzeItem = async (jobId: string, item: ThicknessBatchItem) => {
    const override = overrideConfigs[item.itemId] || {
      lowerMm: item.config.lowerMm,
      upperMm: item.config.upperMm,
      toleranceMm: item.config.judgmentToleranceMm,
    };
    const conditions = thicknessConditionsSchema.safeParse(override);
    if (!conditions.success) {
      setError(conditions.error.issues[0].message);
      return;
    }
    const validated = conditions.data;
    try {
      const updated = await reanalyzeThicknessBatchItem(
        jobId,
        item.itemId,
        {
          ...item.config,
          lowerMm: validated.lowerMm,
          upperMm: validated.upperMm,
          judgmentToleranceMm: validated.toleranceMm,
          finalLowerMm: validated.lowerMm - validated.toleranceMm,
          finalUpperMm: validated.upperMm + validated.toleranceMm,
        },
      );
      setJobs((current) => current.map((job) => (
        job.jobId === updated.jobId ? updated : job
      )));
      setError(null);
    } catch (reanalyzeError) {
      setError(resolveApiErrorMessage(t, reanalyzeError, {
        fallbackKey: 'cad.thickness.batch.reanalyzeFailed',
      }));
    }
  };

  const saveBatchClassification = (updated: ThicknessBatchJob) => {
    setJobs((current) => current.map((job) => (
      job.jobId === updated.jobId ? updated : job
    )));
  };

  return (
    <div className="space-y-2 p-2">
      <CadLimitsPanel status={capabilityState.status} cad={cad} onRetry={capabilityState.retry} />
      {canAnalyze ? (
        <>
          <div className="rounded-md border border-border p-2">
            <button
              type="button"
              className="flex w-full items-center justify-between text-left text-xs font-semibold"
              aria-expanded={showCreateForm}
              onClick={() => setShowCreateForm((current) => !current)}
            >
              {t('cad.thickness.batch.createTitle')}
              <ChevronDown
                className={`size-4 transition-transform ${showCreateForm ? 'rotate-180' : ''}`}
              />
            </button>
            {showCreateForm && <div className="mt-2 space-y-2">
              <div className="grid grid-cols-3 gap-1" aria-label={t('cad.thickness.batch.stepsLabel')}>
        {(['selectFiles', 'conditions', 'submit'] as const).map((step, index) => (
          <div key={step} className="rounded border border-border bg-muted/40 p-1 text-center text-[10px]">
            <strong className="mr-1">{index + 1}</strong>{t(`cad.thickness.batch.steps.${step}`)}
          </div>
        ))}
              </div>

          <input
            ref={inputRef}
            type="file"
            multiple
            accept={canSelect ? cad?.allowedExtensions.join(',') : undefined}
            disabled={!canSelect || submitting}
            onChange={handleFiles}
            hidden
            data-testid="thickness-file-input"
          />
          <Button
            variant="outline"
            className="w-full"
            onClick={() => inputRef.current?.click()}
            disabled={submitting || !canSelect}
          >
            <Upload />{canSelect && cad ? t('cad.thickness.batch.selectFiles', { max: cad.maxBatchFiles }) : t('upload.browseFiles')}
          </Button>
      {files.length > 0 && (
        <div className="rounded-md border border-border p-2 text-xs">
          <p className="font-semibold">{t('cad.thickness.batch.selectedCount', { count: files.length })}</p>
          <p className="mt-0.5 truncate text-muted-foreground">
            {files.map((file) => file.name).join(t('cad.thickness.batch.fileSeparator'))}
          </p>
        </div>
      )}

      <div className="rounded-md border border-border p-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold">{t('cad.thickness.batch.conditionsTitle')}</p>
          <Button size="sm" variant="ghost" onClick={() => setShowDetails((value) => !value)}>
            {showDetails ? t('cad.thickness.batch.hideDetails') : t('cad.thickness.batch.showDetails')}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          {t('cad.thickness.batch.conditionSummary', {
            lower: lowerMm.toFixed(2),
            upper: upperMm.toFixed(2),
            tolerance: toleranceMm.toFixed(2),
          })}
        </p>
        <p className="mt-1 text-[11px] text-muted-foreground">
          {t('cad.thickness.batch.finalRange', {
            lower: (lowerMm - toleranceMm).toFixed(2),
            upper: (upperMm + toleranceMm).toFixed(2),
          })}
        </p>
        {showDetails && (
          <div className="mt-2 grid grid-cols-3 gap-1">
            <div>
              <Label htmlFor="batch-lower" className="text-[10px]">{t('cad.thickness.batch.lower')}</Label>
              <Input id="batch-lower" type="number" min={0} step={0.01} value={lowerMm} onChange={(event) => setLowerMm(Number(event.target.value))} />
            </div>
            <div>
              <Label htmlFor="batch-upper" className="text-[10px]">{t('cad.thickness.batch.upper')}</Label>
              <Input id="batch-upper" type="number" min={0} step={0.01} value={upperMm} onChange={(event) => setUpperMm(Number(event.target.value))} />
            </div>
            <div>
              <Label htmlFor="batch-tolerance" className="text-[10px]">{t('cad.thickness.batch.tolerance')}</Label>
              <Input id="batch-tolerance" type="number" min={0} step={0.01} value={toleranceMm} onChange={(event) => setToleranceMm(Number(event.target.value))} />
            </div>
          </div>
        )}
      </div>

      <Input
        aria-label={t('cad.thickness.batch.nameLabel')}
        placeholder={t('cad.thickness.batch.namePlaceholder')}
        value={name}
        onChange={(event) => setName(event.target.value)}
        disabled={submitting}
      />
      <details className="rounded-md border border-border p-2">
        <summary className="cursor-pointer text-xs font-semibold">
          {t('cad.classification.optionalTitle')}
        </summary>
        <div className="mt-2">
          <CADClassificationFields
            value={classification}
            onChange={setClassification}
            idPrefix="thickness-batch-classification"
            disabled={submitting}
          />
        </div>
      </details>
      <Button
        className="w-full"
        onClick={startBatch}
        disabled={!canSelect || !files.length || submitting || lowerMm > upperMm}
      >
        {submitting ? <RefreshCw className="animate-spin" /> : <Play />}
        {submitting ? t('cad.thickness.batch.submitting') : t('cad.thickness.batch.start')}
      </Button>
      <p className="text-[11px] text-muted-foreground">
        {t('cad.thickness.batch.backgroundNotice')}
      </p>
            </div>}
          </div>

          {acceptedMessage && (
            <Alert variant="success">
              <CheckCircle2 />
              <AlertDescription>{acceptedMessage}</AlertDescription>
            </Alert>
          )}
        </>
      ) : (
        <Alert variant="info">
          <AlertDescription>
            {t('cad.thickness.batch.readOnly')}
          </AlertDescription>
        </Alert>
      )}
      {error && (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {rejectionNotice && (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertDescription className="whitespace-pre-line">{rejectionNotice}</AlertDescription>
        </Alert>
      )}

      <div className="flex items-center justify-between border-t border-border pt-2">
        <p className="text-xs font-bold">{t('cad.thickness.batch.listTitle')}</p>
        <Button size="sm" variant="ghost" onClick={refreshJobs} disabled={loadingJobs}>
          <RefreshCw className={loadingJobs ? 'animate-spin' : ''} />{t('cad.thickness.batch.refresh')}
        </Button>
      </div>
      {jobs.length > 0 && (
        <>
          <Input
            aria-label={t('cad.thickness.batch.partSearchLabel')}
            placeholder={t('cad.thickness.batch.partSearchPlaceholder')}
            value={partSearch}
            onChange={(event) => setPartSearch(event.target.value)}
          />
          <div className="grid grid-cols-2 gap-1.5">
            <label className="sr-only" htmlFor="thickness-machine-filter">
              {t('cad.thickness.batch.machineFilter')}
            </label>
            <select
              id="thickness-machine-filter"
              value={activeMachineFilter}
              onChange={(event) => setMachineFilter(event.target.value)}
              className="h-8 rounded-md border border-input bg-background px-2 text-xs focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">{t('cad.thickness.batch.allMachines')}</option>
              {machineOptions.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
            <label className="sr-only" htmlFor="thickness-number-filter">
              {t('cad.thickness.batch.numberFilter')}
            </label>
            <select
              id="thickness-number-filter"
              value={activeNumberFilter}
              onChange={(event) => setNumberFilter(event.target.value)}
              className="h-8 rounded-md border border-input bg-background px-2 text-xs focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">{t('cad.thickness.batch.allNumbers')}</option>
              {numberOptions.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
          </div>
          <p className="text-[11px] text-muted-foreground">
            {t('cad.thickness.batch.scrollHelp')}
          </p>
        </>
      )}
      {jobs.length === 0 && !loadingJobs && (
        <p className="text-xs text-muted-foreground">{t('cad.thickness.batch.empty')}</p>
      )}
      {hasActiveBatchFilter && filteredJobs.length === 0 && (
        <p className="rounded bg-muted p-2 text-xs text-muted-foreground">
          {t('cad.thickness.batch.noPartSearchResults')}
        </p>
      )}
      {filteredJobs.map((job) => {
        const showAll = showAllJobIds.has(job.jobId);
        const classificationValues = [
          job.name,
          job.classification.folderPath,
          job.classification.machineModel,
          job.classification.managementNumber,
          ...job.classification.tags,
        ];
        const classificationMatchesSearch = normalizedPartSearch
          ? classificationValues.some((value) => (
            normalizeClassificationSearch(value).includes(normalizedPartSearch)
          ))
          : false;
        const searchMatchedItems = normalizedPartSearch && !classificationMatchesSearch
          ? job.items.filter((item) => (
            normalizeClassificationSearch(item.filename).includes(normalizedPartSearch)
          ))
          : null;
        const visibleItems = classificationMatchesSearch
          || activeMachineFilter
          || activeNumberFilter
          ? job.items
          : searchMatchedItems || (showAll
          ? job.items
          : job.items.filter((item) => (
            item.status === 'failed' || item.judgment === 'needs_review'
          )));
        const completedCount = job.items.filter((item) => item.status === 'completed').length;
        const failedCount = job.items.filter((item) => item.status === 'failed').length;
        const needsReviewCount = job.items.filter((item) => item.judgment === 'needs_review').length;
        return (
          <details
            key={job.jobId}
            data-testid={`thickness-batch-${job.jobId}`}
            className="rounded-md border border-border p-2"
            open={hasActiveBatchFilter || expandedJobIds.has(job.jobId)}
            onToggle={(event) => {
              if (hasActiveBatchFilter) {
                // A native <details> toggle mutates the DOM even though `open`
                // is supplied by React. Keep matches visible until search is
                // cleared, including for completed batches that do not poll.
                if (!event.currentTarget.open) event.currentTarget.open = true;
                return;
              }
              const isOpen = event.currentTarget.open;
              setExpandedJobIds((current) => {
                if (current.has(job.jobId) === isOpen) return current;
                const next = new Set(current);
                if (isOpen) next.add(job.jobId);
                else next.delete(job.jobId);
                return next;
              });
            }}
          >
            <summary className="cursor-pointer list-none">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-xs font-semibold">{job.name}</span>
                <Badge variant={job.status === 'failed' ? 'destructive' : ACTIVE_BATCH_STATUSES.has(job.status) ? 'info' : 'secondary'}>
                  {job.status === 'processing' || job.status === 'queued' ? t('cad.thickness.batch.status.processing') : job.status === 'completed' ? t('cad.thickness.batch.status.completed') : job.status === 'completed_with_failures' ? t('cad.thickness.batch.status.completed_with_failures') : t('cad.thickness.batch.status.failed')}
                </Badge>
              </div>
              <Progress value={job.progressPercent} className="mt-1 h-1.5" />
              <p className="mt-1 text-[11px] text-muted-foreground">
                {t('cad.thickness.batch.itemCounts', {
                  needsReview: needsReviewCount,
                  completed: completedCount,
                  failed: failedCount,
                  total: job.items.length,
                })}
              </p>
              {(job.classification.machineModel || job.classification.managementNumber) && (
                <p className="mt-1 truncate text-[11px] font-medium text-foreground/80">
                  {[job.classification.machineModel, job.classification.managementNumber]
                    .filter(Boolean).join(' / ')}
                </p>
              )}
              {(job.classification.folderPath || job.classification.tags.length > 0) && (
                <p className="mt-0.5 truncate text-[10px] text-muted-foreground">
                  {[job.classification.folderPath, ...job.classification.tags]
                    .filter(Boolean).join(' · ')}
                </p>
              )}
            </summary>
            <div className="mt-2 space-y-1">
              <div className="flex justify-between gap-1">
                {hasActiveBatchFilter ? <span /> : (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowAllJobIds((current) => {
                      const next = new Set(current);
                      if (next.has(job.jobId)) next.delete(job.jobId);
                      else next.add(job.jobId);
                      return next;
                    })}
                  >
                    {showAll ? t('cad.thickness.batch.showFlaggedOnly') : t('cad.thickness.batch.showAll')}
                  </Button>
                )}
                <div className="flex gap-1">
                  {job.canEditClassification && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setClassificationJob(job)}
                    >
                      <Pencil />{t('cad.classification.editAction')}
                    </Button>
                  )}
                  {completedCount > 0 && (
                    <Button size="sm" variant="outline" onClick={() => downloadThicknessBatchZip(job.jobId, `${job.name.replace(/[\\/:*?"<>|]+/g, '-')}.zip`)}>
                      <Archive />ZIP
                    </Button>
                  )}
                </div>
              </div>
              {!hasActiveBatchFilter && visibleItems.length === 0 && (
                <p className="rounded bg-muted p-2 text-[11px] text-muted-foreground">
                  {t('cad.thickness.batch.noFlaggedItems')}
                </p>
              )}
              {visibleItems.map((item) => (
                <div key={item.itemId} className="rounded border border-border p-1.5">
                  <div className="flex items-center justify-between gap-1">
                    <span className="truncate text-xs">{item.filename}</span>
                    {itemBadge(item, t)}
                  </div>
                  {item.status === 'failed' && (
                    <p className="mt-1 text-[11px] text-destructive">
                      {resolveApiErrorMessage(t, { code: item.errorCode }, {
                        fallbackKey: 'cad.thickness.errorCodes.analysisFailed',
                      })}
                    </p>
                  )}
                  <div className="mt-1 flex gap-1">
                    {item.status === 'completed' && item.analysisId && (
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => openItem(item)}>
                        <Eye />{t('cad.thickness.batch.openItem')}
                      </Button>
                    )}
                    {item.status === 'failed' && !ACTIVE_BATCH_STATUSES.has(job.status) && (
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => retryItem(job.jobId, item.itemId)}>
                        <RefreshCw />{t('cad.thickness.batch.retryItem')}
                      </Button>
                    )}
                    {item.status !== 'completed' && item.status !== 'failed' && (
                      <span className="flex items-center gap-1 text-[11px] text-muted-foreground"><Clock3 className="size-3" />{t('cad.thickness.batch.itemProcessing')}</span>
                    )}
                  </div>
                  {item.status === 'completed' && !ACTIVE_BATCH_STATUSES.has(job.status) && (
                    <details className="mt-1 rounded bg-muted/50 p-1.5">
                      <summary className="cursor-pointer text-[11px] font-semibold">
                        {t('cad.thickness.batch.reanalyzeTitle')}
                      </summary>
                      <div className="mt-1 grid grid-cols-3 gap-1">
                        {([
                          ['lowerMm', 'lower', item.config.lowerMm],
                          ['upperMm', 'upper', item.config.upperMm],
                          ['toleranceMm', 'tolerance', item.config.judgmentToleranceMm],
                        ] as const).map(([key, labelKey, fallback]) => (
                          <div key={key}>
                            <Label htmlFor={`${item.itemId}-${key}`} className="text-[10px]">{t(`cad.thickness.batch.${labelKey}`)}</Label>
                            <Input
                              id={`${item.itemId}-${key}`}
                              type="number"
                              min={0}
                              step={0.01}
                              value={overrideConfigs[item.itemId]?.[key] ?? fallback}
                              onChange={(event) => setOverrideConfigs((current) => ({
                                ...current,
                                [item.itemId]: {
                                  lowerMm: current[item.itemId]?.lowerMm ?? item.config.lowerMm,
                                  upperMm: current[item.itemId]?.upperMm ?? item.config.upperMm,
                                  toleranceMm: current[item.itemId]?.toleranceMm ?? item.config.judgmentToleranceMm,
                                  [key]: Number(event.target.value),
                                },
                              }))}
                            />
                          </div>
                        ))}
                      </div>
                      <Button size="sm" className="mt-1 w-full" onClick={() => reanalyzeItem(job.jobId, item)}>
                        <RefreshCw />{t('cad.thickness.batch.reanalyze')}
                      </Button>
                    </details>
                  )}
                </div>
              ))}
            </div>
          </details>
        );
      })}
      <ThicknessBatchClassificationDialog
        job={classificationJob}
        onClose={() => setClassificationJob(null)}
        onSaved={saveBatchClassification}
      />
    </div>
  );
};

export default CADThicknessUploadQueue;
